import { Component, Input, OnInit } from '@angular/core';

import { FormControl, FormGroup, Validators } from '@angular/forms';
import { VehicleType } from '@app/enum/VehicleType.enum';
import { IKeyValuePair } from '@app/interfaces/IKeyValuePair';
import { AccountService } from '@app/services/account.service';
import { ContextualSpeedingThresholdService } from '@app/services/contextual-speeding-threshold.service';
import { UnitConversionService, UnitConversionType } from '@app/services/unitConversion.service';
import { ContextualSpeedingEditor } from '../base/contextual-speeding-editor';
import { ContextualSpeedingThreshold } from '../models/contextual-speeding-threshold';
import './contextual-speeding-vehicle-editor.component.scss';
import { SpeedingExample, SpeedingExamplesCalculator } from './speeding-examples-calculator';

@Component({
    selector: 'contextual-speeding-vehicle-editor',
    template: require('./contextual-speeding-vehicle-editor.component.html')
})
export class ContextualSpeedingVehicleEditorComponent 
    extends ContextualSpeedingEditor<ContextualSpeedingThreshold> 
    implements OnInit {
    vehicleTypes: IKeyValuePair<VehicleType>[] = [];
    speedUnit: string;

    form = new FormGroup({
        vehicleType: new FormControl(null, Validators.required),
        speedPercentThreshold: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(100)]),
        speedThresholdAllowance: new FormControl(null, [Validators.required, Validators.min(0), Validators.max(100)]),
    });

    @Input() currentItems: ContextualSpeedingThreshold[] = [];

    get speedingExamples(): SpeedingExample[] {
        const speedPercentThreshold = this.form.get('speedPercentThreshold').value;
        const speedThresholdAllowance = this.form.get('speedThresholdAllowance').value;

        return SpeedingExamplesCalculator.getExamples(speedPercentThreshold, speedThresholdAllowance, this.speedUnit);
    }

    get currentValue(): string {
        if (this.form.invalid) {
            return "";
        }

        const speedThresholdAllowance = this.form.get("speedThresholdAllowance").value;
        const speedPercentThreshold = this.form.get("speedPercentThreshold").value;

        return `${speedThresholdAllowance}${this.speedUnit} + ${speedPercentThreshold}%`
    }

    constructor(
        private readonly thresholdService: ContextualSpeedingThresholdService,
        private readonly accountService: AccountService,
        private readonly unitConversionService: UnitConversionService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.setVehicleTypes();
        this.setSpeedUnit();
        this.optionallySetFormValues();
    }

    async save(): Promise<ContextualSpeedingThreshold> {
        const speedThresholdAllowance = this.form.get("speedThresholdAllowance").value;

        const speedThresholdAllowanceKph = this.unitConversionService
            .convertToDatabaseUnit(speedThresholdAllowance, UnitConversionType.Speed);

        const data: ContextualSpeedingThreshold = {
            Id: this.selectedItem?.Id || 0,
            VehicleType: this.form.get("vehicleType").value,
            SpeedPercentThreshold: this.form.get("speedPercentThreshold").value,
            SpeedThresholdAllowanceKph:speedThresholdAllowanceKph,
            AccountId: this.accountService.getAccountDataFromCache().Id,
        };

        return !!this.selectedItem
            ? await this.thresholdService.updateContextualSpeedingThreshold(data)
            : await this.thresholdService.addContextualSpeedingThreshold(data);
    }

    private setVehicleTypes(): void {
        const currentlyUsedVehicleTypes = this.currentItems.map(x => x.VehicleType);

        this.vehicleTypes = this.thresholdService
            .getAdjustedVehicleTypes()
            .filter(type => {
                const typeIsNotInUse = !currentlyUsedVehicleTypes.includes(type.value);
                const typeIsCurrentlySelected = this.selectedItem?.VehicleType === type.value;

                return typeIsCurrentlySelected || typeIsNotInUse;
            })
    }

    private setSpeedUnit(): void {
        this.speedUnit = this.unitConversionService.getConversionLabel(UnitConversionType.Speed);
    }

    private optionallySetFormValues(): void {
        if (!this.selectedItem) {
            return;
        }

        const percentageValue = this.selectedItem.SpeedPercentThreshold;
        const vehicleType = this.selectedItem.VehicleType;

        const constantValue = this.unitConversionService
            .convertToUserUnit(this.selectedItem.SpeedThresholdAllowanceKph, UnitConversionType.Speed);

        this.form.patchValue({
            vehicleType: vehicleType,
            speedPercentThreshold: percentageValue,
            speedThresholdAllowance: constantValue
        })
    }
}